import { useRouter } from "next/router";
import { User } from "@modules/user/types/user";
import { Popover } from "@headlessui/react";
import { useTranslation } from "next-i18next";
import { LogoutIcon, PersonIcon } from "@talladega/icons";
import Link from "next/link";
import useLogoutUserMutation from "@modules/user/hooks/useLogoutUserMutation";
import CustomerAvatar, {
  Props as CustomerAvatarProps,
} from "@common/components/CustomerAvatar";

type Props = {
  user: User;
  onTrigger?: () => void;
} & Pick<CustomerAvatarProps, "size">;

const UserMenu = ({ user, size = "lg", onTrigger }: Props) => {
  const { t } = useTranslation(["common"]);
  const router = useRouter();
  const logoutMutation = useLogoutUserMutation();

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        router.push("/");
      },
    });
  };

  return (
    <Popover className="relative">
      <Popover.Button onClick={onTrigger}>
        <CustomerAvatar
          customer={user}
          size={size}
          isCurrentUser
          isProfileAvatar
        />
      </Popover.Button>

      <Popover.Panel className="absolute right-0 mt-4 z-40 bg-primary border border-secondary shadow-lg rounded-xl">
        <div className="flex flex-col divide-y divide-secondary py-6">
          <div className="flex items-center gap-4 px-6 pb-4">
            <CustomerAvatar
              customer={user}
              size="lg"
              isCurrentUser
              isProfileAvatar
            />
            <div className="flex flex-col">
              <p className="text-ellipsis whitespace-nowrap overflow-hidden text-sm font-semibold text-secondary">
                <span>{user.firstName}</span>{" "}
                <span data-hj-suppress>{user.lastName}</span>
              </p>
              <p className="text-sm text-tertiary">{user.email}</p>
            </div>
          </div>
          <Link
            className="flex items-center gap-2 px-6 py-4 hover:bg-primary-hover"
            href="/profile"
          >
            <PersonIcon className="size-6 text-quaternary" />
            <span className="text-sm text-secondary">
              {t("common:my_profile")}
            </span>
          </Link>
          <Link
            className="flex items-center gap-2 px-6 pt-4 hover:bg-primary-hover"
            href="#"
            onClick={() => handleLogout()}
          >
            <LogoutIcon className="size-6 text-error-primary" />
            <span className="text-sm text-secondary">{t("common:logout")}</span>
          </Link>
        </div>
      </Popover.Panel>
    </Popover>
  );
};

export default UserMenu;
